import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type BackendDiscount = {
	id: string;
	orgId: string;
	monthsFrom: number;
	monthsTo: number | null;
	discountPct: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	isDeleted: boolean;
};

export type FrontendDiscountTier = {
	id: string;
	monthsFrom: number;
	monthsTo: number | null;
	discountPct: number;
};

export type SaveDiscountPayload = {
	monthsFrom: number;
	monthsTo?: number | null;
	discountPct: string;
};

function discountScaleDetail(id: string): string {
	return `${endpoints.payments.discountScale}/${id}`;
}

function isLocalId(id: string) {
	return id.startsWith("tier-");
}

function mapBackendToFrontend(d: BackendDiscount): FrontendDiscountTier {
	return {
		id: d.id,
		monthsFrom: d.monthsFrom,
		monthsTo: d.monthsTo,
		discountPct: Number.parseFloat(d.discountPct),
	};
}

function frontendToSavePayload(d: FrontendDiscountTier): SaveDiscountPayload {
	return {
		monthsFrom: d.monthsFrom,
		monthsTo: d.monthsTo,
		discountPct: d.discountPct.toFixed(2),
	};
}

export async function getDiscountTiers(): Promise<FrontendDiscountTier[]> {
	const response = await apiClient
		.get<{ data: BackendDiscount[] }>(endpoints.payments.discountScale)
		.catch((error) => {
			console.error("[EarlyPaymentDiscountsApi] Failed to fetch discount tiers:", error);
			return { data: { data: [] as BackendDiscount[] } };
		});
	return (response.data.data ?? []).map(mapBackendToFrontend);
}

export async function saveDiscountTiers(
	current: FrontendDiscountTier[],
	previous: FrontendDiscountTier[]
): Promise<FrontendDiscountTier[]> {
	const previousMap = new Map(previous.filter((d) => !isLocalId(d.id)).map((d) => [d.id, d]));

	const results: FrontendDiscountTier[] = [];

	for (const tier of current) {
		if (isLocalId(tier.id)) {
			const res = await apiClient
				.post<{ data: BackendDiscount }>(
					endpoints.payments.discountScale,
					frontendToSavePayload(tier)
				)
				.catch((error) => {
					console.error("[EarlyPaymentDiscountsApi] Failed to create discount tier:", error);
					throw error;
				});
			results.push(mapBackendToFrontend(res.data.data));
		} else {
			const prev = previousMap.get(tier.id);
			if (
				prev &&
				(prev.monthsFrom !== tier.monthsFrom ||
					prev.monthsTo !== tier.monthsTo ||
					prev.discountPct !== tier.discountPct)
			) {
				const res = await apiClient
					.patch<{ data: BackendDiscount }>(
						discountScaleDetail(tier.id),
						frontendToSavePayload(tier)
					)
					.catch((error) => {
						console.error("[EarlyPaymentDiscountsApi] Failed to update discount tier:", error);
						throw error;
					});
				results.push(mapBackendToFrontend(res.data.data));
			} else {
				results.push(tier);
			}
			previousMap.delete(tier.id);
		}
	}

	for (const removed of previousMap.values()) {
		await apiClient.delete(discountScaleDetail(removed.id)).catch((error) => {
			console.error("[EarlyPaymentDiscountsApi] Failed to delete discount tier:", error);
			throw error;
		});
	}

	return results;
}
